import {
  AscProjectTpTicketsViewSort,
  GetAscProjectTpTicketsQuery,
  GetAscProjectTpTicketsQueryVariables,
  GET_ASC_PROJECT_TP_TICKETS,
  keywordFilter,
  useCursorPaginatedQuery,
  TicketGroupingKind,
  AscProjectTpTicketsViewFilter,
} from '@neo.tax/shared';

import { useAscContext } from '../context';
import { AscGroupingTicketOrCollection } from './useAscProjectGroupingTicketsOrCollections';

export type AscProjectTpTicket = GetAscProjectTpTicketsQuery['getAscProjectTpTickets']['edges'][number]['node'];

const groupingFilter = (groupingTicketOrCollection: AscGroupingTicketOrCollection): AscProjectTpTicketsViewFilter => {
  if (groupingTicketOrCollection.kind === TicketGroupingKind.GroupingCollection) {
    return {
      groupingTicketingProviderCollectionId: { eq: groupingTicketOrCollection.id },
    };
  }

  if (groupingTicketOrCollection.kind === TicketGroupingKind.GroupingTicket) {
    return {
      groupingTicketingProviderTicketId: { eq: groupingTicketOrCollection.id },
    };
  }

  return {
    groupingTicketingProviderCollectionId: { is: null },
    groupingTicketingProviderTicketId: { is: null },
  };
};

type UseTicketsForAscProjectProps = {
  ascProjectId: string;
  searchKeyword?: string | null;
  skip?: boolean;
  sorting?: Array<AscProjectTpTicketsViewSort> | AscProjectTpTicketsViewSort;
  numElementsPerFetch?: number;
  groupingTicketOrCollection?: AscGroupingTicketOrCollection;
};

export const useTicketsForAscProject = ({
  ascProjectId,
  groupingTicketOrCollection,
  numElementsPerFetch = 30,
  searchKeyword,
  skip = false,
  sorting,
}: UseTicketsForAscProjectProps): {
  hasNextPage: boolean;
  loadMore: () => Promise<void>;
  loading: boolean;
  tickets: AscProjectTpTicket[] | null;
} => {
  const { fiscalYearId, periodName } = useAscContext();

  // The grouping query surfaces an epic/collection either because its own name/identifier
  // matched, or because one of its children matched. When the parent itself matches we skip
  // the child keyword filter so the user can browse the whole grouping; when only a child
  // matched we keep the filter so the matching ticket isn't lost in a list of siblings.
  const isInsideRealGrouping =
    groupingTicketOrCollection?.kind === TicketGroupingKind.GroupingTicket ||
    groupingTicketOrCollection?.kind === TicketGroupingKind.GroupingCollection;
  const lowerKeyword = searchKeyword?.toLowerCase() ?? '';
  const parentMatchesKeyword =
    isInsideRealGrouping &&
    !!searchKeyword &&
    (!!groupingTicketOrCollection?.name?.toLowerCase().includes(lowerKeyword) ||
      !!groupingTicketOrCollection?.identifier?.toLowerCase().includes(lowerKeyword));

  const {
    data,
    error,
    hasNextPage,
    loading,
    onLoadMoreItems: loadMore,
  } = useCursorPaginatedQuery<GetAscProjectTpTicketsQuery, GetAscProjectTpTicketsQueryVariables>(
    GET_ASC_PROJECT_TP_TICKETS,
    {
      variables: {
        ascProjectId,
        fiscalYearId,
        periodName,
        sorting,
        filter: {
          and: [
            keywordFilter(
              ['name', 'identifier', 'contributorName'] satisfies (keyof AscProjectTpTicket)[],
              parentMatchesKeyword ? null : searchKeyword,
            ),
            ...(groupingTicketOrCollection ? [groupingFilter(groupingTicketOrCollection)] : []),
          ],
        },
      },
      skip,
    },
    {
      getConnection: (data) => data.getAscProjectTpTickets,
      numElementsPerFetch,
    },
  );

  const tickets = data?.getAscProjectTpTickets?.edges.map((edge) => edge.node) ?? [];

  if (loading) {
    return { loading: true, tickets, hasNextPage, loadMore };
  }
  if (error) {
    throw error;
  }

  return { loading: false, tickets, hasNextPage, loadMore };
};
